import { effect, inject, Injectable, OnDestroy, signal } from '@angular/core';
import { currentTheme, getTheme } from 'devextreme/viz/themes';
import { BehaviorSubject, Subscription } from 'rxjs';
import { SiaParamsAndPatternsService } from './params-and-patterns.service';
import { getRuntimeConfig } from './runtime-config';

@Injectable({
  providedIn: 'root',
})
export class ThemeService implements OnDestroy {
  private paramsAndPatternsService = inject(SiaParamsAndPatternsService);

  themeChanged: BehaviorSubject<string> = new BehaviorSubject('');
  #baseThemeColor = signal<string>('');
  #variantThemeColor = signal<string>('');
  body = document.getElementsByTagName('body')[0];

  #themeColorSubscription?: Subscription;

  #applyThemeEffect = effect(() => {
    const baseThemeColor = this.#baseThemeColor();
    const variantThemeColor = this.#variantThemeColor();
    this.applyTheme();
    this.themeChanged.next(`${baseThemeColor}-${variantThemeColor}`);
  });

  #persistThemeEffect = effect(() => {
    const runtime = getRuntimeConfig();
    const base = this.#baseThemeColor();
    const variant = this.#variantThemeColor();
    if (base === runtime.theme.base) {
      localStorage.removeItem('sia-theme-base');
    } else {
      localStorage.setItem('sia-theme-base', base);
    }
    if (variant === runtime.theme.variant) {
      localStorage.removeItem('sia-theme-variant');
    } else {
      localStorage.setItem('sia-theme-variant', variant);
    }
  });

  constructor() {
    this.#themeColorSubscription = this.paramsAndPatternsService.themeColorChanged$.subscribe((color: string) => {
      if (color) this.#baseThemeColor.set(color);
    });
    const runtime = getRuntimeConfig();
    this.#variantThemeColor.set(localStorage.getItem('sia-theme-variant') || runtime.theme.variant);
    this.#baseThemeColor.set(localStorage.getItem('sia-theme-base') || runtime.theme.base);
  }

  getThemeItem(...keys: Array<string>): any {
    const theme = getTheme(currentTheme());
    let item = theme;
    for (let key in keys) item = item[keys[key]];
    return item;
  }

  setThemeVariant(theme: string) {
    this.#variantThemeColor.set(theme);
  }

  setThemeColor(color: string) {
    this.#baseThemeColor.set(color);
  }

  applyTheme() {
    this.body.className = '';

    if (this.#variantThemeColor() === 'dark') {
      this.body.className += `dark`;
    } else {
      this.body.className += `light`;
    }
    this.body.className += ` ${this.#baseThemeColor()}-theme`;
  }

  getCurrentTheme(): string {
    return this.#variantThemeColor();
  }

  ngOnDestroy(): void {
    this.#themeColorSubscription?.unsubscribe();
  }
}
